import { useEffect, useMemo, useState } from "react";
import { FileUp, Loader2, Plus, Search } from "lucide-react";
import { type ColumnDef, type SortingState } from "@tanstack/react-table";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { API_BASE } from "@/config/api";
import { fetchWithAuth } from "@/lib/api";
import { cn } from "@/lib/utils";
import type { Asset, Book, Record, RecordWithAssets } from "@/schemas";
import { BulkTransactionModal } from "@/components/modals/bulk-transaction-modal";
import { EditRecordModal } from "@/components/modals/edit-record-modal";
import { ImportTransactionsModal } from "@/components/modals/import-transactions-modal";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { DeleteConfirmationDialog } from "@/components/ui/delete-confirmation-dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getAsset } from "@/features/portfolio/services/asset-service";
import { portfolioKeys } from "@/features/portfolio/hooks/queries";
import { DataTable } from "@/features/portfolio/components/data-table";
import { getColumns } from "@/features/transactions/components/columns";

interface ScopedRecordWithAssets extends RecordWithAssets {
  portfolio_name?: string;
}

interface PortfolioTransactionsTabProps {
  selectedBookId: string;
  books: Book[];
}

export function PortfolioTransactionsTab({ selectedBookId, books }: PortfolioTransactionsTabProps) {
  const queryClient = useQueryClient();
  const [records, setRecords] = useState<ScopedRecordWithAssets[]>([]);
  const [isLoadingRecords, setIsLoadingRecords] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [sorting, setSorting] = useState<SortingState>([{ id: "record_date", desc: true }]);

  const [recordToDelete, setRecordToDelete] = useState<string | null>(null);
  const [recordToEdit, setRecordToEdit] = useState<RecordWithAssets | null>(null);

  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);

  useEffect(() => {
    void fetchScopedRecords();
  }, [selectedBookId, books]);

  const fetchRecordsByBook = async (bookId: string, portfolioName?: string): Promise<ScopedRecordWithAssets[]> => {
    const response = await fetchWithAuth(`${API_BASE}/records/book/${bookId}?expand=assets`);
    if (!response.ok) {
      if (response.status === 404 || response.status === 204) {
        return [];
      }
      throw new Error(`Failed to fetch records for ${bookId}`);
    }

    const payload = await response.json();
    if (!Array.isArray(payload)) return [];

    const transformedRecords: ScopedRecordWithAssets[] = payload.map((record: RecordWithAssets) => ({
      ...record,
      buyAsset: record.buyAsset || undefined,
      sellAsset: record.sellAsset || undefined,
      asset: record.asset || undefined,
      currencyAsset: record.currencyAsset || undefined,
      portfolio_name: portfolioName,
    }));

    const feeAssetIds = new Set<string>();
    transformedRecords.forEach((record) => {
      record.fees?.forEach((fee) => feeAssetIds.add(fee.asset_id));
    });

    const feeAssetsMap: { [key: string]: Asset } = {};
    if (feeAssetIds.size > 0) {
      await Promise.all(
        Array.from(feeAssetIds).map(async (assetId) => {
          const asset = await getAsset(assetId);
          if (asset) {
            feeAssetsMap[assetId] = asset;
          }
        }),
      );
    }

    return transformedRecords.map((record) => ({
      ...record,
      feeAssets: feeAssetsMap,
    }));
  };

  const fetchScopedRecords = async () => {
    try {
      setIsLoadingRecords(true);
      if (selectedBookId === "global") {
        const result = await Promise.all(
          books.map((book) => fetchRecordsByBook(book.book_id, book.name)),
        );
        const merged = result.flat().sort((a, b) => {
          const aTs = new Date(a.record_date).getTime();
          const bTs = new Date(b.record_date).getTime();
          return bTs - aTs;
        });
        setRecords(merged);
      } else {
        const selected = books.find((book) => book.book_id === selectedBookId);
        const scoped = await fetchRecordsByBook(selectedBookId, selected?.name);
        setRecords(scoped);
      }
    } catch (error) {
      console.error("Error fetching records:", error);
      setRecords([]);
    } finally {
      setIsLoadingRecords(false);
    }
  };

  const refreshPortfolioData = async () => {
    await queryClient.invalidateQueries({ queryKey: portfolioKeys.all });
  };

  const refreshTransactionsAndPortfolio = async () => {
    await Promise.all([fetchScopedRecords(), refreshPortfolioData()]);
  };

  const handleCreateTransactions = async (bookId: string, recordsToCreate: Partial<Record>[]) => {
    const recordsWithBookId = recordsToCreate.map((record) => ({
      book_id: bookId,
      ...record,
    }));

    const response = await fetchWithAuth(`${API_BASE}/records/bulk`, {
      method: "POST",
      body: JSON.stringify(recordsWithBookId),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || "Failed to create transactions");
    }
    await refreshTransactionsAndPortfolio();
  };

  const handleDeleteRecord = async () => {
    if (!recordToDelete) return;
    const response = await fetchWithAuth(`${API_BASE}/records/${recordToDelete}`, {
      method: "DELETE",
    });
    if (!response.ok) {
      throw new Error("Failed to delete transaction");
    }
    await refreshTransactionsAndPortfolio();
  };

  const handleUpdateRecord = async (recordId: string, data: Record) => {
    const response = await fetchWithAuth(`${API_BASE}/records/${recordId}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      throw new Error("Failed to update transaction");
    }
    await refreshTransactionsAndPortfolio();
  };

  const filteredRecords = useMemo(() => {
    return records.filter((record) => {
      const matchesSelectedPortfolio =
        selectedBookId === "global" || record.book_id === selectedBookId;
      const matchesSearch =
        !searchQuery ||
        record.buyAsset?.symbol?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        record.sellAsset?.symbol?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        record.asset?.symbol?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        record.description?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesType = typeFilter === "all" || record.type === typeFilter;

      return matchesSelectedPortfolio && matchesSearch && matchesType;
    });
  }, [records, searchQuery, selectedBookId, typeFilter]);

  const stats = useMemo(() => {
    let buyCount = 0;
    let sellCount = 0;
    filteredRecords.forEach((record) => {
      if (record.type === "buy") buyCount += 1;
      if (record.type === "sell") sellCount += 1;
    });
    return {
      totalCount: filteredRecords.length,
      buyCount,
      sellCount,
    };
  }, [filteredRecords]);

  const showPortfolioColumn = selectedBookId === "global";
  const baseColumns = getColumns({
    onDeleteRecord: (recordId) => setRecordToDelete(recordId),
    onEditRecord: (record) => setRecordToEdit(record),
  }) as ColumnDef<ScopedRecordWithAssets>[];
  const columns: ColumnDef<ScopedRecordWithAssets>[] = showPortfolioColumn
    ? [
        {
          accessorKey: "portfolio_name",
          header: "Portfolio",
          cell: ({ row }) => (
            <Badge variant="outline" className="font-medium">
              {row.original.portfolio_name ?? "Unknown"}
            </Badge>
          ),
        },
        ...baseColumns,
      ]
    : baseColumns;

  const activeBookId = selectedBookId === "global" ? "" : selectedBookId;
  const notifyPortfolioSelectionRequired = () => {
    toast.info("Select a portfolio first", {
      description: "Choose a specific portfolio in the header before creating or importing transactions.",
    });
  };

  return (
    <Card className="shadow-sm border-border/40">
      <CardHeader className="space-y-4">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle>Transactions</CardTitle>
            <CardDescription>
              {selectedBookId === "global"
                ? "All portfolio transactions in one view"
                : `Transaction history for ${books.find((book) => book.book_id === selectedBookId)?.name ?? "portfolio"}`}
            </CardDescription>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              onClick={() => {
                if (!activeBookId) {
                  notifyPortfolioSelectionRequired();
                  return;
                }
                setIsBulkModalOpen(true);
              }}
              className="gap-2"
            >
              <Plus className="h-4 w-4" />
              Add Transaction
            </Button>

            {activeBookId ? (
              <ImportTransactionsModal
                onSuccess={refreshTransactionsAndPortfolio}
                book_id={activeBookId}
              />
            ) : (
              <Button
                variant="outline"
                className="h-10 border-muted/60"
                onClick={notifyPortfolioSelectionRequired}
              >
                <FileUp className="mr-2 h-4 w-4" />
                Import
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2 md:gap-3">
          <div className="rounded-lg border border-border/40 bg-muted/20 p-3">
            <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wider whitespace-nowrap">Total Actions</p>
            <p className="text-xl font-bold">{stats.totalCount}</p>
          </div>
          <div className="rounded-lg border border-border/40 bg-muted/20 p-3">
            <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wider whitespace-nowrap">Buy</p>
            <p className="text-xl font-bold text-emerald-600">{stats.buyCount}</p>
          </div>
          <div className="rounded-lg border border-border/40 bg-muted/20 p-3">
            <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wider whitespace-nowrap">Sell</p>
            <p className="text-xl font-bold text-red-600">{stats.sellCount}</p>
          </div>
        </div>

        <div className="flex flex-col gap-3 md:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder="Search by symbol or note..."
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
            />
          </div>

          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className={cn("w-full md:w-[180px]")}>
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="buy">Buy</SelectItem>
              <SelectItem value="sell">Sell</SelectItem>
              <SelectItem value="exchange">Exchange</SelectItem>
              <SelectItem value="currency_conversion">FX Conversion</SelectItem>
              <SelectItem value="deposit">Deposit</SelectItem>
              <SelectItem value="withdrawal">Withdrawal</SelectItem>
              <SelectItem value="dividend">Dividend</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>

      <CardContent>
        {isLoadingRecords ? (
          <div className="flex items-center justify-center py-14">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : filteredRecords.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border/60 bg-muted/10 px-6 py-14 text-center">
            <p className="text-base font-semibold">No transactions yet</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Add your first transaction to start tracking this portfolio.
            </p>
            <div className="mt-5 flex justify-center gap-2">
              <Button
                onClick={() => {
                  if (!activeBookId) {
                    notifyPortfolioSelectionRequired();
                    return;
                  }
                  setIsBulkModalOpen(true);
                }}
                variant="outline"
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Transaction
              </Button>
              {activeBookId ? (
                <ImportTransactionsModal
                  onSuccess={refreshTransactionsAndPortfolio}
                  book_id={activeBookId}
                />
              ) : (
                <Button
                  variant="outline"
                  className="h-10 border-muted/60"
                  onClick={notifyPortfolioSelectionRequired}
                >
                  <FileUp className="mr-2 h-4 w-4" />
                  Import
                </Button>
              )}
            </div>
          </div>
        ) : (
          <DataTable
            columns={columns}
            data={filteredRecords}
            sorting={sorting}
            onSortingChange={setSorting}
          />
        )}
      </CardContent>

      {activeBookId && (
        <BulkTransactionModal
          open={isBulkModalOpen}
          onClose={() => {
            setIsBulkModalOpen(false);
            void fetchScopedRecords();
          }}
          bookId={activeBookId}
          bookType={books.find((book) => book.book_id === activeBookId)?.book_type}
          onSubmit={handleCreateTransactions}
        />
      )}

      {recordToEdit && (
        <EditRecordModal
          open={!!recordToEdit}
          onClose={() => setRecordToEdit(null)}
          record={recordToEdit}
          bookType={books.find((book) => book.book_id === selectedBookId)?.book_type}
          onSubmit={handleUpdateRecord}
        />
      )}

      <DeleteConfirmationDialog
        open={!!recordToDelete}
        onOpenChange={(open) => {
          if (!open) setRecordToDelete(null);
        }}
        title="Delete transaction"
        description="This transaction will be removed from your history."
        itemName={recordToDelete ?? ""}
        onConfirm={handleDeleteRecord}
        confirmButtonText="Delete"
      />
    </Card>
  );
}
